import type { Ref } from 'vue'
import { onBeforeUnmount, onMounted } from 'vue'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

const openModals: HTMLElement[] = []

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(
    (element) => element.tabIndex >= 0 && element.getClientRects().length > 0
  )
}

export function useModalFocus(containerRef: Ref<HTMLElement | null>): void {
  const activeOnSetup = document.activeElement
  const previouslyFocused =
    activeOnSetup instanceof HTMLElement && activeOnSetup !== document.body
      ? activeOnSetup
      : null

  const onKeyDown = (event: KeyboardEvent): void => {
    const container = containerRef.value
    if (event.key !== 'Tab' || !container) return
    if (openModals[openModals.length - 1] !== container) return

    const focusables = focusableElements(container)
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement

    if (!first || !last) {
      event.preventDefault()
      container.focus({ preventScroll: true })
      return
    }

    const focusIsOnEdge =
      active === container ||
      !(active instanceof Node && container.contains(active))

    if (event.shiftKey && (focusIsOnEdge || active === first)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && (focusIsOnEdge || active === last)) {
      event.preventDefault()
      first.focus()
    }
  }

  onMounted(() => {
    const container = containerRef.value
    if (!container) return

    openModals.push(container)
    document.addEventListener('keydown', onKeyDown, true)

    const active = document.activeElement
    if (!(active instanceof Node && container.contains(active))) {
      container.focus({ preventScroll: true })
    }
  })

  onBeforeUnmount(() => {
    const container = containerRef.value
    document.removeEventListener('keydown', onKeyDown, true)

    const index = container ? openModals.indexOf(container) : -1
    if (index !== -1) openModals.splice(index, 1)

    const active = document.activeElement
    const focusIsInside =
      !!container && active instanceof Node && container.contains(active)
    const focusIsLost = !active || active === document.body

    if ((focusIsInside || focusIsLost) && previouslyFocused?.isConnected) {
      previouslyFocused.focus({ preventScroll: true })
    }
  })
}
